import _ from 'lodash'
import { Idle } from 'tmr/services/idle'

/**
 * Polls a single resource or an array of resources
 *
 * The polling interval is determined by {@link tmrResourcePollingInterval}.
 * When passing an array of resources, assumes that the current route is a resourceList, that is:
 * - In the route definition, has resourceList.searchParameters and, optionally, searchAction
 * - In the scope, has a $ctrl.resources array.
 * To pause polling, pass `false` instead of an object or array.
 *
 * @example <caption>Single resource</caption>
 * <div tmr-resource-polling="$ctrl.resource">{{$ctrl.resource.$getLabel()}}</div>
 *
 * @example <caption>Multiple resources</caption>
 * <div tmr-resource-polling="$ctrl.resources">...</div>
 *
 * @example <caption>Pause polling</caption>
 * <div tmr-resource-polling="$ctrl.poll ? $ctrl.resources : false">...</div>
 *
 * @namespace
 * @alias tmrResourcePolling
 * @type {ngDirective}
 */
function tmrResourcePollingDirective(
  $injector,
  $interval,
  $parse,
  $state,
  $stateParams,
  $transitions,
  TmrPaging,
  tmrResourcePollingInterval
) {
  'ngInject'
  return {
    restrict: 'A',
    link,
  }

  function link($scope, $element, $attrs) {
    let fn = $parse($attrs.tmrResourcePolling)
    // Randomize interval by 2% to avoid clobbering requests from multiple directives.
    let delay = _.random(
      tmrResourcePollingInterval,
      tmrResourcePollingInterval + tmrResourcePollingInterval * 0.02
    )

    const idle = new Idle()
    idle.start()

    let poll = $interval(() => {
      // Skip if page is idle or invisible.
      if (!idle.visible || idle.idle) return

      let resource = fn($scope)

      // Skip if expression compiles to false
      if (resource === false) return

      // Skip if already polling
      if (resource.$polling) return

      resource.$polling = true

      let promise

      // Resource list
      if (Array.isArray(resource)) {
        let {
          current: { params, resourceType, resourceList },
        } = $state
        let resourceService = $injector.get(
          resource[0] ? resource[0].$getType() : resourceType
        )
        let queryParams = TmrPaging.resolveParams(
          $stateParams,
          _.pick(
            $stateParams,
            resourceList.searchParameters,
            resourceList.hiddenSearchParameters
          )
        )
        let action = params.searchAction ?? 'pagedQuery'
        if (typeof action === 'object') action = action.value
        promise = resourceService[action](queryParams).$promise.then(
          loadedResources => {
            // If a resource with the same id was already loaded, re-use the old object,
            // so references (in components etc.) are not lost.
            loadedResources.forEach((loadedResource, idx) => {
              let prevResource = resource.find(
                ({ id }) => id == loadedResource.id
              )
              if (prevResource !== undefined)
                loadedResources[idx] = Object.assign(
                  prevResource,
                  loadedResource.toJSON()
                )
            })
            resource.length = 0
            resource.push(...loadedResources)
          }
        )
      }
      // Single resource
      else {
        promise = resource.$get()
      }

      promise.finally(() => (resource.$polling = false))
    }, delay)

    const cancel = () => {
      idle.stop()
      $interval.cancel(poll)
    }

    $transitions.onExit({}, cancel, { invokeLimit: 1 })
    $element.on('$destroy', cancel)
  }
}

export const directive = ['tmrResourcePolling', tmrResourcePollingDirective]
